import * as THREE from 'three';
import { Random } from '../../core/Random';
import { box, cyl, M, mat, mesh, registerNightGlow, texMat, worldBox } from '../materials';
import {
  americanFlagTexture,
  blindsTexture,
  cardboardTexture,
  foilTexture,
  glassTexture,
  latticeTexture,
  roofTexture,
  rustySidingTexture,
  sidingTexture,
  woodTexture,
} from '../textures';

export type WindowKind = 'glass' | 'dim' | 'foil' | 'cardboard' | 'flag' | 'blinds' | 'boarded';

export interface WindowSpec {
  x: number;
  side: 1 | -1;
  kind?: WindowKind;
  w?: number;
  h?: number;
}

export interface TrailerSpec {
  length?: number;
  width?: number;
  height?: number;
  color: string;
  trim?: string;
  stripe?: string;
  door?: string;
  /** Local x of the door, on the +z wall. */
  doorX: number;
  windows?: WindowSpec[];
  /** Local x of a window AC unit on the +z wall. */
  ac?: number;
  skirting?: 'lattice' | 'metal' | 'none';
  steps?: 'blocks' | 'deck' | 'none';
  awning?: boolean;
  /** Which end (+1/-1 along x) gets the hitch tongue; 0 for none. */
  hitch?: number;
  tiresOnRoof?: boolean;
  tarp?: boolean;
  rusty?: boolean;
  porchLight?: boolean;
  seed?: number;
}

export const TRAILER_BASE = 0.55;

let glassLit: THREE.MeshStandardMaterial | null = null;
let glassDim: THREE.MeshStandardMaterial | null = null;
let porchBulb: THREE.MeshStandardMaterial | null = null;

function glass(lit: boolean): THREE.MeshStandardMaterial {
  if (lit && !glassLit) {
    glassLit = new THREE.MeshStandardMaterial({
      map: glassTexture(),
      roughness: 0.25,
      metalness: 0.1,
      emissive: new THREE.Color('#ffbd5a'),
      emissiveIntensity: 0,
    });
    registerNightGlow(glassLit, 1.1);
  }
  if (!lit && !glassDim) {
    glassDim = new THREE.MeshStandardMaterial({
      map: glassTexture(),
      roughness: 0.25,
      metalness: 0.1,
      emissive: new THREE.Color('#7a8fb8'),
      emissiveIntensity: 0,
    });
    registerNightGlow(glassDim, 0.35);
  }
  return (lit ? glassLit : glassDim) as THREE.MeshStandardMaterial;
}

export function porchBulbMaterial(): THREE.MeshStandardMaterial {
  if (!porchBulb) {
    porchBulb = new THREE.MeshStandardMaterial({ color: '#fff3c4', emissive: new THREE.Color('#ffd27a') });
    registerNightGlow(porchBulb, 2.2);
  }
  return porchBulb;
}

export function windowMaterial(kind: WindowKind): THREE.Material {
  switch (kind) {
    case 'foil':
      return texMat(foilTexture(), { rough: 0.3, metal: 0.6 });
    case 'cardboard':
      return texMat(cardboardTexture());
    case 'flag': {
      const m = texMat(americanFlagTexture(), { emissive: '#ffffff', emissiveIntensity: 0 });
      registerNightGlow(m, 0.35);
      return m;
    }
    case 'blinds': {
      const m = texMat(blindsTexture(), { emissive: '#ffd08a', emissiveIntensity: 0 });
      registerNightGlow(m, 0.6);
      return m;
    }
    case 'dim':
      return glass(false);
    case 'boarded':
      return texMat(woodTexture(), { color: '#b89a6a' });
    default:
      return glass(true);
  }
}

/**
 * A classic single-wide. Local frame: long axis = x, door on the +z wall,
 * origin at ground level under the trailer's center.
 */
export function buildTrailer(spec: TrailerSpec): THREE.Group {
  const L = spec.length ?? 12;
  const W = spec.width ?? 4;
  const H = spec.height ?? 2.5;
  const base = TRAILER_BASE;
  const rng = new Random(spec.seed ?? 1);
  const g = new THREE.Group();
  const trim = mat(spec.trim ?? '#f4f1e6');

  const siding = texMat(spec.rusty ? rustySidingTexture() : sidingTexture(), { color: spec.color });
  g.add(mesh(worldBox(L, H, W, 1), siding, 0, base + H / 2, 0));
  if (spec.stripe)
    g.add(box(L + 0.02, 0.2, W + 0.02, mat(spec.stripe), 0, base + H * 0.63, 0, { cast: false }));
  g.add(box(L + 0.03, 0.1, W + 0.03, trim, 0, base + 0.05, 0, { cast: false }));
  g.add(box(L + 0.03, 0.08, W + 0.03, trim, 0, base + H - 0.04, 0, { cast: false }));

  const roof = texMat(roofTexture(), { rough: 0.7, metal: 0.2 });
  g.add(mesh(worldBox(L + 0.12, 0.12, W + 0.25, 2), roof, 0, base + H + 0.06, 0));
  g.add(mesh(worldBox(L + 0.04, 0.07, W * 0.55, 2), roof, 0, base + H + 0.15, 0));
  g.add(box(0.5, 0.18, 0.5, mat('#d9d6cc'), -L * 0.25, base + H + 0.2, 0));

  // Skirting hides the wheels and the crimes under the trailer.
  const skirt = spec.skirting ?? 'lattice';
  if (skirt === 'lattice') {
    g.add(
      mesh(worldBox(L - 0.1, base, W - 0.1, 0.6), texMat(latticeTexture()), 0, base / 2, 0, { cast: false }),
    );
  } else if (skirt === 'metal') {
    g.add(
      mesh(
        worldBox(L - 0.1, base, W - 0.1, 1),
        texMat(sidingTexture(), { color: '#a8a39a' }),
        0,
        base / 2,
        0,
        { cast: false },
      ),
    );
  } else {
    for (let x = -L / 2 + 0.8; x <= L / 2 - 0.5; x += 2.4) {
      g.add(box(0.4, base, 0.2, M.cinder, x, base / 2, W / 2 - 0.4));
      g.add(box(0.4, base, 0.2, M.cinder, x, base / 2, -W / 2 + 0.4));
    }
    g.add(box(L - 0.3, 0.12, W - 0.3, M.rubber, 0, base - 0.06, 0, { cast: false }));
  }

  if (spec.hitch) {
    const s = spec.hitch;
    const tip = s * (L / 2 + 1.3);
    for (const zSide of [-1, 1]) {
      const bar = box(1.5, 0.12, 0.12, M.black, s * (L / 2 + 0.62), 0.42, zSide * 0.42);
      bar.rotation.y = zSide * s * 0.62;
      g.add(bar);
    }
    g.add(cyl(0.06, 0.06, 0.42, M.steel, tip - s * 0.2, 0.21, 0, 8));
    g.add(box(0.2, 0.06, 0.2, M.black, tip, 0.42, 0));
  }

  // Front door + steps.
  const doorZ = W / 2;
  g.add(box(0.92, 2.02, 0.07, trim, spec.doorX, base + 1.01, doorZ + 0.02));
  g.add(box(0.82, 1.94, 0.07, mat(spec.door ?? '#e9e2cf'), spec.doorX, base + 1.0, doorZ + 0.04));
  const dw = mesh(
    new THREE.PlaneGeometry(0.4, 0.35),
    glass(rng.chance(0.6)),
    spec.doorX,
    base + 1.55,
    doorZ + 0.085,
    { cast: false },
  );
  g.add(dw);
  g.add(box(0.06, 0.06, 0.06, M.chrome, spec.doorX + 0.3, base + 0.95, doorZ + 0.1, { cast: false }));

  const steps = spec.steps ?? 'blocks';
  if (steps === 'blocks') {
    g.add(box(0.95, 0.2, 0.42, M.cinder, spec.doorX, 0.1, doorZ + 0.62));
    g.add(box(0.95, 0.2, 0.42, M.cinder, spec.doorX, 0.1, doorZ + 0.22));
    g.add(box(0.95, 0.2, 0.42, M.cinder, spec.doorX, 0.3, doorZ + 0.22));
  } else if (steps === 'deck') {
    const wood = texMat(woodTexture());
    g.add(mesh(worldBox(3.4, 0.12, 2.3, 1), wood, spec.doorX, base - 0.06, doorZ + 1.15));
    for (const dx of [-1.6, 1.6])
      for (const dz of [0.15, 2.2])
        g.add(box(0.12, base - 0.12, 0.12, M.trunk, spec.doorX + dx, (base - 0.12) / 2, doorZ + dz));
    for (const dx of [-1.6, 1.6])
      g.add(box(0.08, 1.0, 0.08, M.trunk, spec.doorX + dx, base + 0.5, doorZ + 2.2));
    g.add(box(1.1, 0.07, 0.07, M.trunk, spec.doorX - 1.1, base + 0.95, doorZ + 2.2));
    g.add(box(1.1, 0.07, 0.07, M.trunk, spec.doorX + 1.1, base + 0.95, doorZ + 2.2));
    g.add(box(1.0, 0.25, 0.45, wood, spec.doorX, 0.13, doorZ + 2.55));
  }

  if (spec.awning) {
    const aw = box(
      2.8,
      0.06,
      1.9,
      mat('#e8e4d6', { side: THREE.DoubleSide }),
      spec.doorX,
      base + 2.42,
      doorZ + 0.92,
    );
    aw.rotation.x = 0.1;
    g.add(aw);
    g.add(box(2.82, 0.16, 0.04, mat('#3f7a4a'), spec.doorX, base + 2.32, doorZ + 1.86, { cast: false }));
    for (const dx of [-1.32, 1.32])
      g.add(cyl(0.035, 0.035, base + 2.35, M.chrome, spec.doorX + dx, (base + 2.35) / 2, doorZ + 1.82, 6));
  }

  if (spec.porchLight) {
    g.add(box(0.14, 0.2, 0.1, M.black, spec.doorX - 0.7, base + 2.1, doorZ + 0.06, { cast: false }));
    g.add(
      mesh(
        new THREE.SphereGeometry(0.07, 8, 6),
        porchBulbMaterial(),
        spec.doorX - 0.7,
        base + 2.02,
        doorZ + 0.12,
        { cast: false },
      ),
    );
  }

  for (const w of spec.windows ?? []) {
    const ww = w.w ?? 1.1;
    const wh = w.h ?? 0.85;
    const z = w.side * (W / 2 + 0.015);
    g.add(box(ww + 0.14, wh + 0.14, 0.05, trim, w.x, base + 1.6, z, { cast: false }));
    const pane = mesh(
      new THREE.PlaneGeometry(ww, wh),
      windowMaterial(w.kind ?? 'glass'),
      w.x,
      base + 1.6,
      w.side * (W / 2 + 0.045),
      {
        cast: false,
      },
    );
    if (w.side < 0) pane.rotation.y = Math.PI;
    g.add(pane);
    if (w.kind === 'boarded') {
      for (const tilt of [-0.55, 0.55]) {
        const board = box(
          ww * 1.25,
          0.14,
          0.04,
          texMat(woodTexture(), { color: '#9c7e52' }),
          w.x,
          base + 1.6,
          w.side * (W / 2 + 0.08),
          {
            cast: false,
          },
        );
        board.rotation.z = tilt;
        g.add(board);
      }
    }
  }

  if (spec.ac !== undefined) {
    g.add(box(0.66, 0.44, 0.58, mat('#d8d3c2'), spec.ac, base + 1.42, doorZ + 0.28));
    g.add(box(0.56, 0.3, 0.02, mat('#8e8a7e'), spec.ac, base + 1.42, doorZ + 0.575, { cast: false }));
    g.add(
      box(
        0.12,
        0.6,
        0.02,
        mat('#6b5a3a', { transparent: true, opacity: 0.45 }),
        spec.ac + 0.18,
        base + 0.9,
        doorZ + 0.012,
        { cast: false },
      ),
    );
  }

  if (spec.tarp) {
    const tarp = box(
      3.4,
      0.04,
      W * 0.85,
      mat('#1f5fbf', { rough: 0.6 }),
      rng.range(-L / 4, L / 4),
      base + H + 0.22,
      0,
    );
    tarp.rotation.y = rng.range(-0.08, 0.08);
    g.add(tarp);
  }
  if (spec.tiresOnRoof) {
    for (let i = 0; i < 3; i++) {
      const t = mesh(
        new THREE.TorusGeometry(0.3, 0.12, 6, 12),
        M.rubber,
        -L / 3 + i * (L / 3),
        base + H + 0.32,
        rng.range(-0.8, 0.8),
      );
      t.rotation.x = Math.PI / 2;
      g.add(t);
    }
  }
  return g;
}
